import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PaginationControl } from '@repo/shared-client/components/blocks'
import { Card, CardContent, Separator } from '@repo/shared-client/components/ui'
import { createFileRoute, stripSearchParams, useSearch } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import AdminDeletedAccountReasonsStats from '@web/src/features/admin/admin-deleted-account-reasons-stats/admin-deleted-account-reasons-stats'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { enumToString } from '@web/src/lib/utilsClient'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type {
  AccountDeletionReasonsListPageQuery,
  AccountDeletionReasonsListPageQueryVariables,
  DeletionReasonEnum,
} from 'types/graphql'
import { z } from 'zod'

dayjs.extend(relativeTime)

const ACCOUNT_DELETION_REASONS_QUERY: TypedDocumentNode<
  AccountDeletionReasonsListPageQuery,
  AccountDeletionReasonsListPageQueryVariables
> = gql`
  query AccountDeletionReasonsListPageQuery($page: Int) {
    accountDeletionReasons: accountDeletionReasonsListPage(page: $page) {
      entries {
        id
        createdAt
        updatedAt
        reason
        customReason
      }
      count
      perPage
    }
  }
`

interface DeletionReasonItemProps {
  reason: {
    id: string
    createdAt?: string
    reason?: DeletionReasonEnum
    customReason?: string
  }
}

const DeletionReasonCard = ({ reason }: DeletionReasonItemProps) => {
  const { t } = useTranslation()
  return (
    <Card className="w-full">
      <CardContent className="grid grid-cols-[70px_1fr] gap-1">
        <p className="font-bold">{t('Reason')}:</p>
        <p className="capitalize">{enumToString(reason?.reason)}</p>

        {reason?.customReason ? (
          <>
            <p className="font-bold">{t('Details')}:</p>
            <p className="w-full whitespace-pre-line rounded-md bg-muted p-2">{reason?.customReason}</p>
          </>
        ) : null}

        <p className="font-bold">{t('Deleted')}:</p>
        <p>
          {dayjs(reason?.createdAt).format('MMMM D YYYY, h:mm:ss a')} ({dayjs(reason?.createdAt).fromNow()})
        </p>
      </CardContent>
    </Card>
  )
}

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/deletedaccounts')({
  component: DeletedAccountReasonsPage,
  validateSearch: z.object({
    page: z.number().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
      }),
    ],
  },
  loaderDeps: ({ search: { page } }) => ({
    page,
  }),
  loader: async ({ context, deps }) => {
    const variables: AccountDeletionReasonsListPageQueryVariables = {
      page: deps.page ? deps.page : 1,
    }
    const { queryRef } = await queryAndPreload(context, ACCOUNT_DELETION_REASONS_QUERY, {
      variables,
    })
    return { queryRef }
  },
  head: () =>
    seo({
      title: `Admin - Deleted Account Reasons | ${env.VITE_APP_NAME}`,
      description: 'Admin Deleted Account Reasons Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function DeletedAccountReasonsPage() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<AccountDeletionReasonsListPageQuery>)

  const search = useSearch({ strict: false })
  const page = search.page ? Number.parseInt(String(search.page), 10) : 1

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />

  const accountDeletionReasons = data.accountDeletionReasons

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col gap-6">
        <AdminDeletedAccountReasonsStats />

        <Separator className="w-full" />

        <div className="flex w-full flex-col gap-4">
          <p className="self-start font-bold text-xl">{t('Recent Account Deletions')}</p>

          {accountDeletionReasons?.entries?.map((r) => (
            <DeletionReasonCard key={r.id} reason={r} />
          ))}

          <PaginationControl
            count={accountDeletionReasons?.count ? accountDeletionReasons.count : 1}
            page={page || 1}
            pageSize={accountDeletionReasons?.perPage ? accountDeletionReasons.perPage : 1}
          />
        </div>
      </div>
    </div>
  )
}
